/**
 * ContextShellOrganism
 *
 * visual-coverage-allow: a route-level shell, not a leaf component — it owns
 * nine live data hooks (useContextView, useContextSelection,
 * useAvailableContexts, useContextPermissions, useContextViewPermissions,
 * useViewPersonalization, useLevelFilters, useSharedFilterLink,
 * useAvailableTypes) that fetch against a real backend, plus a hard `Router`
 * ancestor requirement (`useHref`/`useLocation`, see below). Storying it in
 * isolation would mean either faking nine hooks' worth of network state or
 * hitting a live API from Storybook, neither of which this design-system's
 * story environment supports today (no MSW/API-mock harness). Its visual
 * surface is exercised through the visualizations that mount it
 * (TreeMapOrganism et al.), which do have stories.
 *
 * Owns context selection, view resolution, the session layer, filters,
 * personalization, shared links, the switcher, the shared-link banner and
 * the view-editor overlay — every concern EAINT-780/789/793 each bolted onto
 * `TreeMapOrganism` because the TreeMap was the only visualization that
 * needed it. A visualization receives resolved config, row-affecting filter
 * state, and a chrome node it places wherever its own layout wants
 * (`chrome.contextBar`, present only when `chromePlacement="slot"`).
 *
 * Config resolution (later overrides earlier, via `resolveViewConfig`):
 *   TREEMAP_DEFAULT_*_CONFIG < ContextView config + inline prop overrides < user overlay < session layer
 *
 * Context resolution order:
 *   contextSystemNameOverride → localStorage (per sphereId) → contextSystemName → TREEMAP_DEFAULT_CONTEXT_SYSTEM_NAME
 *
 * Shared filter links (EAINT-793 Phase 4). The shell owns `useSharedFilterLink`
 * directly — a host no longer threads `linkFilters`/`onExitSharedLink`/
 * `buildShareUrl` through props, so any page that mounts this shell (a route
 * or a metaspace grid) inherits `?f=`/`?view=` support with no per-host wiring.
 * `buildShareUrl` is built here via `useHref`, which is router-strategy-agnostic
 * (correct under `HashRouter` and `BrowserRouter` alike) — a `location.pathname`
 * reconstruction would silently reintroduce EAINT-793 review pass 5's critical.
 * This makes a `Router` ancestor a hard requirement for every host.
 *
 * D-3 (specs/visualization-agnostic-context-shell.md): a `?f=` envelope is
 * addressed to one `contextSystemName`. A page can mount several shells (a
 * metaspace hosting two TreeMaps, possibly over different contexts), and a
 * shell ignores a link that is not addressed to its own `activeContextSystemName`
 * — it neither suppresses that shell's own layers nor renders the banner.
 * Addressing is decided once per link, against the context the shell was
 * showing when that link arrived, and holds only while it still shows that
 * context; see `linkAudience` below for why both halves are load-bearing.
 * `?view=` obeys the same address — it is written only as part of a shared
 * link, so an unaddressed shell must not let it override its host's pin.
 *
 * Addressing separates shells over *different* contexts. Two boards over the
 * *same* context are both legitimately in the link's audience, and stay in
 * step because `active` is derived from the live `?f=` param rather than
 * latched per instance — one `exit()` deletes the param and deactivates all of
 * them. See `useSharedFilterLink`.
 */

import React, { useEffect, useState, useCallback, useRef, useMemo, useContext, type ReactNode } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { useHref, useLocation } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useContextView } from '../../../hooks/useContextView';
import { useContextSelection } from '../../../hooks/useContextSelection';
import { useAvailableContexts } from '../../../hooks/useAvailableContexts';
import { useContextPermissions } from '../../../hooks/useContextPermissions';
import { useContextViewPermissions, resetContextViewPermissionsCache } from '../../../hooks/useContextViewPermissions';
import { useViewPersonalization } from '../../../hooks/useViewPersonalization';
import { useLevelFilters } from '../../../hooks/useLevelFilters';
import { useSharedFilterLink } from '../../../hooks/useSharedFilterLink';
import { useAvailableTypes } from '../../../hooks/useAvailableTypes';
import { useContextDirtyState } from '../TreeMap/useContextDirtyState';
import {
  getContext,
  updateContext,
  cloneContext,
  type ContextViewRecord,
} from '../../../api/contextApi';
import ContextSwitcherToolbarMolecule from '../../molecules/ContextSwitcherToolbar/ContextSwitcherToolbarMolecule';
import { globalSphereCache } from '@center-platform/spheres/cache';
import ContextFilterBarMolecule, {
  type ContextFilterBarLevelEntry,
} from '../../molecules/ContextFilterBar/ContextFilterBarMolecule';
import FilterSummaryGroupMolecule from '../../molecules/FilterSummaryGroup/FilterSummaryGroupMolecule';
import SharedLinkBannerMolecule, {
  type SharedLinkBannerNotice,
} from '../../molecules/SharedLinkBanner/SharedLinkBannerMolecule';
import ContextViewEditorOverlayOrganism from '../ContextViewEditorOverlay/ContextViewEditorOverlayOrganism';
import MessageAtom from '../../atoms/Message/MessageAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import './ContextShellOrganism.scss';
import {
  TREEMAP_DEFAULT_VIEW_CONFIG,
  TREEMAP_DEFAULT_BASE_CONFIG,
  mergeRecordByKey,
} from '../TreeMap/defaults';
import { resolveViewConfig } from '../../../utils/resolveViewConfig';
import ViewStateContext, { ViewStateBoundary, useViewState } from '../../../context/ViewStateContext';
import { currentRenderingToViewForm } from '../../../utils/contextViewFormMapping';
import type { FilterSaveTarget, FilterSaveTargetAvailability } from '../../../hooks/useLevelFilters';
import {
  buildFilterPropertyOptions,
  buildFilterPerspectiveOptions,
  buildFilterRelationshipOptions,
  type SchemaForFilters,
} from '../../../utils/filterPropertyBuilder';
import type { Context, RuntimeFilterEnvelope, SharedFilterLinkEnvelope } from 'types/context';
import type { UnifiedFilters } from 'types/filters';
import type { TreeMapViewConfig, BaseViewConfig, ComposedLevelFilters } from 'types/contextView';
import type { ContextViewFormState } from '../ContextViewEditor/ContextViewEditorOrganism';
import {
  encodeFilterLink,
  filterLinkEncodedLength,
  hasVariableToken,
  SHARE_LINK_MAX_CHARS,
} from '../../../utils/filterLink';

/** System name used when no contextSystemName prop is supplied and localStorage has no selection. */
export const TREEMAP_DEFAULT_CONTEXT_SYSTEM_NAME = 'center_treemap_default_context';

/**
 * The resolved state + row-affecting handlers a visualization body needs.
 * Mirrors the shell-owned subset of `TreeMapProps` (`TreeMap/types.ts`) so an
 * adapter can spread this straight into its visualization rather than
 * re-deriving any of it (spec R2 — a duplicated derivation is how the
 * mirror-half defect pattern EAINT-789 hit five times reappears).
 */
export interface ContextShellBodyState {
  context: Context;
  viewConfig: TreeMapViewConfig;
  baseConfig: BaseViewConfig;
  levelFilters: Record<string, ComposedLevelFilters>;
  runtimeFilters?: RuntimeFilterEnvelope;
  filterSaveTargets: FilterSaveTargetAvailability[];
  onSetLevelFilter: (levelId: string, filters: UnifiedFilters, target: FilterSaveTarget) => void;
  /** Whether an active shared filter link is suppressing this level's own view/user/session layers (EAINT-793). */
  linkSuppressed: boolean;
  /** Leaves shared-link mode — for every shell on the page, since they all read the one `?f=`. Present only while this shell is in link mode; the shell owns the link now, so there is no host prop gating it. */
  onExitSharedLink?: () => void;
  /** Whether the caller holds `edit` on the CONTEXT — raw, not merged with the view permission (G-3: the switch belongs to the toolbar, not the body). */
  canEditContext: boolean;
  /** Navigates to the context editor for this level's context — omitted entirely (never called) when the host passed no `onEditContext`. */
  onEditContext: (levelId: string) => void;
  /** Fires when a column popover's "Share with view…" is confirmed for the given level — receives that level's own user-tier clauses (EAINT-936). */
  onShareWithView: (levelId: string, clauses: UnifiedFilters) => Promise<void>;
  /** Merges a patch into the session layer (board arrangement) — never the dirty rail. */
  patchSessionView: (patch: Partial<TreeMapViewConfig>) => void;
  /** Merges a patch into the debounced, persisted personalization overlay — the other half of the same board interaction. */
  patchPersonalizationView: (patch: Partial<TreeMapViewConfig>) => void;
  /** Available property options for column filter popovers — scoped to the sphere hierarchy. */
  filterPropertyOptions: import('../../molecules/PropertyFilter/PropertyFilterMolecule').PropertyOption[];
  /** Available perspective options for column filter popovers. */
  filterPerspectiveOptions: import('../../molecules/PerspectiveFilter').PerspectiveOption[];
  /** Label resolvers for column filter popovers. */
  getFilterPropertyLabel: (propertyKey: string, aspect?: string) => string;
  getFilterPerspectiveLabel: (systemName: string) => string;
  getFilterPropertyValueLabel: (propertyKey: string, value: string) => string;
}

export interface ContextShellChrome {
  /** Switcher + shared-link banner + filter bar. Non-null only when the host declares `chromePlacement="slot"`. */
  contextBar: ReactNode | null;
  body: ContextShellBodyState;
}

export interface ContextShellOrganismProps {
  /** Component default context system name. Used when the user has no localStorage selection and no override. Defaults to TREEMAP_DEFAULT_CONTEXT_SYSTEM_NAME when omitted. */
  contextSystemName?: string;
  /** Hard override — bypasses localStorage (e.g., a metaspace template that pins a context) */
  contextSystemNameOverride?: string;
  /** Organization ID for context lookup */
  organizationId: string;
  /** Root sphere ID to start the hierarchy from */
  rootSphereId: string;
  /**
   * Optional ContextView system name for view-specific config. Overridden by
   * a `?view=` query param, if present (EAINT-793) — the shell reads its own
   * `?view=`/`?f=` via `useSharedFilterLink` (Phase 4), so a host no longer
   * threads the URL's requested view through this prop itself.
   */
  viewSystemName?: string;
  /** Inline TreeMapViewConfig overrides — merged into the `view` layer, below the session layer */
  viewConfig?: Partial<TreeMapViewConfig>;
  /** Inline BaseViewConfig overrides — merged into the `view` layer, below the session layer */
  baseConfig?: Partial<BaseViewConfig>;
  /** Navigate to the "New context" creation form */
  onNewContext?: () => void;
  /** Navigate to the context management page */
  onManageContexts?: () => void;
  /** Navigate to the context editor for the given context — the "Edit in context" affordance. */
  onEditContext?: (contextSystemName: string) => void;
  /** Fired when the filter-settings (sliders) icon button in the toolbar is clicked */
  onFilterSettings?: () => void;
  /**
   * D-1: how the shell knows whether the visualization consumed
   * `chrome.contextBar`. Explicit and typed rather than detected — a
   * visualization that forgets to declare anything still shows its filter
   * bar, because `'above'` (the shell renders the bar itself, above the
   * body) is the default, not the opt-in.
   */
  chromePlacement?: 'slot' | 'above';
  /** Renders the visualization body, given the resolved chrome. */
  children: (chrome: ContextShellChrome) => ReactNode;
  /**
   * Render prop for the inline context editor. When provided, clicking
   * "Open context" from the view editor opens the context editor inline
   * (inside the board area) instead of navigating away via onEditContext.
   */
  renderContextEditor?: (params: {
    contextSystemName: string;
    onBack: () => void;
    onClose: () => void;
  }) => ReactNode;
}

/**
 * Aggregate lane counts across the whole envelope (`global` plus every
 * `byLevelId` entry) for the `context_filter_link_shared` analytics event —
 * counts and sizes only, never a property name or filter value (spec §
 * Analytics: "Filter values routinely contain sphere ids and identity ids").
 */
function sumEnvelopeLaneCounts(envelope: RuntimeFilterEnvelope) {
  const counts = { property: 0, perspective: 0, relationship: 0, arc: 0, group: 0 };
  const addLanes = (filters: UnifiedFilters | undefined) => {
    if (!filters) return;
    counts.property += filters.propertyFilters?.length ?? 0;
    counts.perspective += filters.perspectiveFilters?.length ?? 0;
    counts.relationship += filters.relationshipFilters?.length ?? 0;
    counts.arc += filters.arcFilters?.length ?? 0;
    counts.group += filters.filterGroups?.length ?? 0;
  };
  addLanes(envelope.global);
  Object.values(envelope.byLevelId ?? {}).forEach(addLanes);
  return counts;
}

/**
 * Wraps {@link ContextShellOrganismInner} in a `ViewStateBoundary` so the
 * shell can both provide and consume the session layer: a component cannot
 * read a context it renders itself, so a provider must sit one level above
 * the function body that calls `useViewState`.
 *
 * A *boundary*, not a `ViewStateProvider`: the session layer is required to
 * survive a visualization switch on the same context
 * (`specs/view-personalization-overlay.md` § The session layer), which means
 * the real provider belongs on an ancestor that stays mounted — the app
 * shell or route — and is exported for that purpose. Mounting one here
 * unconditionally would shadow it and quietly reinstate the visualization's
 * own lifetime. The boundary defers when a provider exists above and
 * supplies one only for standalone use (Storybook, read-only embeds, tests).
 */
const ContextShellOrganism: React.FC<ContextShellOrganismProps> = (props) => (
  <ViewStateBoundary>
    <ContextShellOrganismInner {...props} />
  </ViewStateBoundary>
);

const ContextShellOrganismInner: React.FC<ContextShellOrganismProps> = (props) => {
  const {
    contextSystemName,
    contextSystemNameOverride,
    organizationId,
    rootSphereId,
    viewSystemName: viewSystemNameProp,
    viewConfig: inlineViewConfig,
    baseConfig: inlineBaseConfig,
    onNewContext,
    onManageContexts,
    onEditContext,
    onFilterSettings,
    chromePlacement = 'above',
    children,
    renderContextEditor,
  } = props;

  const { t, i18n } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [rootSphereName, setRootSphereName] = useState<string | undefined>();
  useEffect(() => {
    if (!rootSphereId || !apiClients?.graphClient) return;
    globalSphereCache.getSphereData(rootSphereId, apiClients.graphClient).then((data) => {
      const name = data.info.sphere?.properties?.name ?? data.info.properties?.name;
      if (name) setRootSphereName(name);
    }).catch(() => { /* display-name only; fall back gracefully */ });
  }, [rootSphereId, apiClients]);
  const toastRef = useRef<Toast>(null);
  const logAnalyticsEvent = useAnalytics();

  // ── Shared filter link (EAINT-793 Phase 4) — `?f=`/`?view=` on the host's URL ──
  const sharedLink = useSharedFilterLink();
  // Pulled out by name: `exit` is the hook's own stable `useCallback`, while
  // `sharedLink` itself is a new object literal each render.
  const { exit: exitSharedLink } = sharedLink;
  const [malformedDismissed, setMalformedDismissed] = useState(false);

  // Dismissal applies to the link that was dismissed, not to the shell for the
  // rest of its life — otherwise a second malformed link later in the session
  // is silently swallowed by the first one's dismissal.
  useEffect(() => {
    setMalformedDismissed(false);
  }, [sharedLink.linkId]);

  // Share's URL builder. `useHref` is router-strategy-agnostic — it resolves
  // against a `HashRouter` and a `BrowserRouter` alike, so this code never has
  // to name which one it is under. Resolving it against `location.href` yields
  // a correct absolute URL either way. Building it from `location.pathname`
  // instead would silently reintroduce EAINT-793 review pass 5's critical: a
  // broken link under this app's `HashRouter`.
  const routerLocation = useLocation();
  const routeHref = useHref(routerLocation.pathname);
  const buildShareUrl = useCallback(
    (params: URLSearchParams) => new URL(`${routeHref}?${params.toString()}`, globalThis.location.href).toString(),
    [routeHref]
  );

  // ── Schema catalog (degraded-filter detection's property source) ──────────
  const { schemas, loading: schemasLoading } = useAvailableTypes(apiClients, rootSphereId);

  // ── Context Selection (localStorage-scoped per sphere) ────────────────────
  const { defaultContext: resolvedDefaultContext } = useAvailableContexts(
    apiClients,
    rootSphereId
  );
  const defaultContextSystemName =
    contextSystemName ??
    resolvedDefaultContext?.contextSystemName ??
    TREEMAP_DEFAULT_CONTEXT_SYSTEM_NAME;

  // A `?f=` addressed to the very context this shell's route already names makes
  // the URL unambiguous, so it must outrank a stale localStorage pick. Without
  // this, a recipient who had previously switched this sphere's board to another
  // context opens the link and silently gets their OWN context with no filters,
  // no banner and no notice — a board that looks like a normal successful load
  // while showing something other than what was shared. That is the one
  // unrecoverable failure mode the shared-link design forbids.
  //
  // Scope is deliberately tight: an explicit `contextSystemNameOverride` still
  // outranks the link, and a link addressed to a context this shell does not
  // route to is still ignored here — D-3's sibling-shell separation is
  // untouched. Note this gate does cover metaspace boards, which pin through
  // `contextSystemName` rather than the override; that is harmless, since the
  // link must name the pinned context for the match to hold at all.
  const linkAddressMatchesRoute =
    contextSystemName !== undefined && sharedLink.filters?.contextSystemName === contextSystemName;

  const { activeContextSystemName, setActiveContextSystemName } = useContextSelection({
    sphereId: rootSphereId,
    scopeKey: 'treemap',
    defaultContextSystemName,
    initialOverride:
      contextSystemNameOverride ?? (linkAddressMatchesRoute ? contextSystemName : undefined),
  });

  // ── Is this shell the link's audience? (D-3 addressing) ────────────────────
  // Decided once per link, from the context this shell was showing when that
  // link arrived — not re-decided on every later context switch.
  //
  // Re-deciding continuously would mean a `?f=` that addresses a context no
  // mounted shell is currently showing simply sits in the URL, decoded and
  // dormant, and retroactively takes over a board the moment the user happens
  // to switch it to the addressed context — suppressing the layers they had
  // set, from a link they had already navigated past. A shared link addresses
  // the board you *landed on*; it does not lie in wait for one.
  //
  // Keyed on `linkId` rather than on mount so a link that arrives after mount
  // (a pasted URL, no remount) is still decided freshly, against the context
  // showing at that moment. `activeContextSystemName` is resolved
  // synchronously by `useContextSelection` (override → localStorage → default),
  // so there is no window where this reads an unresolved value.
  const [linkAudience, setLinkAudience] = useState<{ linkId: string | undefined; contextSystemName: string }>(
    () => ({ linkId: sharedLink.linkId, contextSystemName: activeContextSystemName })
  );
  if (linkAudience.linkId !== sharedLink.linkId) {
    setLinkAudience({ linkId: sharedLink.linkId, contextSystemName: activeContextSystemName });
  }

  // Both halves must hold: this shell was the addressee when the link arrived,
  // AND it still shows that context. The second half is what makes switching
  // away drop the link, which is correct — a link addressed to one context was
  // never meant to narrow another.
  const linkIsAddressedToThisShell =
    sharedLink.filters?.contextSystemName === activeContextSystemName &&
    sharedLink.filters?.contextSystemName === linkAudience.contextSystemName;

  // `?view=` travels inside a shared link — `handleShare` is the only producer
  // of the param, and it only ever writes it alongside `?f=`. So it obeys the
  // same address: pre-Phase-4 one host read it and threaded it into one board,
  // but every shell now reads the URL, and an unaddressed `?view=` outranking
  // a host-pinned `viewSystemName` would retarget every other board on a
  // metaspace page onto a view from a context it does not show — a 403/404
  // that silently drops that board's pinned layer to defaults with Save
  // disabled, and which its banner can never explain because that shell is not
  // in link mode.
  const effectiveViewSystemName = linkIsAddressedToThisShell
    ? sharedLink.requestedViewSystemName ?? viewSystemNameProp
    : viewSystemNameProp;

  // ── Context loading ────────────────────────────────────────────────────────
  const [context, setContext] = useState<Context | null>(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);

  useEffect(() => {
    setContext(null);
    setContextError(null);
  }, [activeContextSystemName]);

  useEffect(() => {
    if (!apiClients?.schemaClient || !activeContextSystemName) return;

    let cancelled = false;
    setContextLoading(true);

    getContext(apiClients.schemaClient, activeContextSystemName)
      .then((result) => {
        if (!cancelled) setContext(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err);
          setContextError(message);
        }
      })
      .finally(() => {
        if (!cancelled) setContextLoading(false);
      });

    return () => { cancelled = true; };
  }, [apiClients, activeContextSystemName]);

  // ── Active ContextView (runtime-switchable; seeded from `?view=`/the viewSystemName prop) ─
  const [activeViewSystemName, setActiveViewSystemName] = useState<string | null>(
    effectiveViewSystemName ?? null
  );

  useEffect(() => {
    setActiveViewSystemName(effectiveViewSystemName ?? null);
  }, [effectiveViewSystemName]);

  const [viewRefreshKey, setViewRefreshKey] = useState(0);
  const {
    view: fetchedView,
    loading: viewLoading,
    error: viewError,
    errorStatus: viewErrorStatus,
    refreshing: viewRefreshing,
  } = useContextView(apiClients, activeViewSystemName ?? undefined, viewRefreshKey);

  const [viewOverride, setViewOverride] = useState<ContextViewRecord | null>(null);
  useEffect(() => {
    setViewOverride(null);
  }, [activeViewSystemName]);
  const view = viewOverride ?? fetchedView;

  const activeViewDisplayName = useMemo(() => {
    if (!view) return undefined;
    const lang = i18n.language;
    return view.name?.translations?.[lang] || view.name?.default || view.system_name;
  }, [view, i18n.language]);

  // ── Permissions ────────────────────────────────────────────────────────────
  // The edit gate moves with the resource being saved: once a saved view is
  // active, `edit` resolves through the VIEW — see the toolbar's
  // `canEditActiveContext` below. `canEditContext` (raw) still reaches the
  // body unswitched (G-3): the in-grid popover's "Edit in context" gate
  // checks CONTEXT edit regardless of which view is active.
  const { canEdit: canEditContext } = useContextPermissions(apiClients, activeContextSystemName);
  const { canEdit: canEditActiveView, loading: canEditActiveViewLoading } = useContextViewPermissions(
    apiClients,
    activeViewSystemName ?? undefined
  );
  const canEditActiveContext = activeViewSystemName ? canEditActiveView : canEditContext;

  // ── Shared filter link, addressed (D-3) ────────────────────────────────────
  // `sharedLink.filters` is decoded whenever `?f=` is present and well-formed,
  // regardless of which context it names — every shell on the page decodes the
  // same URL param independently. Only the addressed shell treats it as
  // active; every other shell (a second board on the same metaspace, over a
  // different context) sees `undefined` here, exactly as if no link were
  // present at all — no suppression, no banner.
  const linkFilters: SharedFilterLinkEnvelope | undefined = linkIsAddressedToThisShell
    ? sharedLink.filters
    : undefined;

  // ── Per-level runtime filters (EAINT-781) ──────────────────────────────────
  const handleLevelFilterWriteError = useCallback(() => {
    toastRef.current?.show({
      severity: 'warn',
      summary: t('contextSwitcher.personalizationSaveError'),
      life: 4000,
    });
  }, [t]);

  // A 403 on a filter write means the `canEditActiveView` this shell is holding
  // is stale — bump the cache epoch so the next lookup re-fetches instead of
  // leaving the view fail-closed for the rest of the session.
  const handleViewPermissionDenied = useCallback(() => {
    resetContextViewPermissionsCache();
  }, []);

  const {
    byLevel: levelFiltersByLevel,
    runtimeFilters,
    saveTargets: filterSaveTargets,
    setLevelFilter,
    hasPendingSharedFilters,
    pendingSharedFilterLevelIds,
    commitPendingSharedFilters,
    discardPendingSharedFilters,
    reset: resetLevelFilters,
    degraded: degradedLevelFilters,
    canRemoveLayer,
    removeFilterClause,
  } = useLevelFilters({
    apiClients,
    contextSystemName: activeContextSystemName,
    contextDefinition: context?.definition,
    viewSystemName: activeViewSystemName,
    view,
    canEditView: canEditActiveView,
    canEditViewLoading: canEditActiveViewLoading,
    onViewUpdated: setViewOverride,
    onWriteError: handleLevelFilterWriteError,
    onViewPermissionDenied: handleViewPermissionDenied,
    schemas: schemas as SchemaForFilters[],
    schemasLoading,
    contextApplicability: context?.applicability,
    t,
    linkFilters,
  });

  // ── Shared filter link (EAINT-793) ─────────────────────────────────────────
  const linkActive = linkFilters !== undefined;
  const linkHasVariableToken = useMemo(() => hasVariableToken(linkFilters), [linkFilters]);

  const sharedLinkNotice = useMemo<SharedLinkBannerNotice>(() => {
    if (!linkActive) return 'none';

    // `activeViewSystemName`, not the `viewSystemName` prop: the two diverge as
    // soon as the active view resolves from the stored selection or the
    // switcher, and reading the prop would attribute an unrelated view fetch's
    // failure to the shared link — or miss the link's own.
    if (activeViewSystemName && !viewLoading && !view && viewError) {
      // Only a definite 403/404 is a REFUSAL. "An outage is not a refusal", and
      // a refusal always arrives as an explicit 403/404 — so the burden of
      // proof sits on the refusal branch, not the outage branch. Two prior
      // review passes had to close this at both ends: `=== 503` sent 502/504
      // and network failures (no `errorStatus` at all) to `viewUnresolved`,
      // and `>= 400 && < 500` did the same to the 401 this route returns for a
      // missing identity/org profile and to the rate limiter's 429. Both
      // variants told the recipient the sender's view is unavailable TO THEM,
      // and withheld the retry, for transient conditions that have nothing to
      // do with their permissions. Do not widen this test.
      const isRefusal = viewErrorStatus === 403 || viewErrorStatus === 404;
      return isRefusal ? 'viewUnresolved' : 'viewUnavailable';
    }

    const requestedLevelIds = Object.keys(linkFilters?.byLevelId ?? {});
    if (requestedLevelIds.length > 0 && context) {
      const contextLevelIds = new Set((context.definition?.levels ?? []).map((level) => level.id));
      const anyMatches = requestedLevelIds.some((levelId) => contextLevelIds.has(levelId));
      if (!anyMatches) return 'noLevelMatch';
    }

    return 'none';
  }, [linkActive, activeViewSystemName, viewLoading, view, viewError, viewErrorStatus, linkFilters, context]);

  const linkAnalyticsFiredForRef = useRef(false);
  const linkActivatedAtRef = useRef<number | null>(null);

  const handleExitSharedLink = useCallback(() => {
    const dwellMs = linkActivatedAtRef.current !== null ? Date.now() - linkActivatedAtRef.current : 0;
    logAnalyticsEvent('context_filter_link_exited', undefined, {
      contextSystemName: activeContextSystemName ?? null,
      dwellMs,
    });
    exitSharedLink();
    // `exitSharedLink`, not the whole `sharedLink` object — the hook returns a
    // fresh object literal every render, so depending on the object made this
    // callback (and the whole memoized chain hanging off it, down to the
    // level-filter popover's deferred-save effect) new on every render.
  }, [activeContextSystemName, logAnalyticsEvent, exitSharedLink]);

  const linkViewRetryCountRef = useRef(0);

  const handleRetrySharedLinkView = useCallback(() => {
    linkViewRetryCountRef.current += 1;
    logAnalyticsEvent('context_filter_link_view_retry', undefined, {
      contextSystemName: activeContextSystemName ?? null,
      attempt: linkViewRetryCountRef.current,
    });
    resetContextViewPermissionsCache();
    setViewRefreshKey((key) => key + 1);
  }, [activeContextSystemName, logAnalyticsEvent]);

  const handleSetLevelFilterWithLinkExit = useCallback(
    (levelId: string, filters: UnifiedFilters, target: FilterSaveTarget) => {
      if (linkActive) handleExitSharedLink();
      setLevelFilter(levelId, filters, target);
    },
    [linkActive, handleExitSharedLink, setLevelFilter]
  );

  // "Share with view…" (EAINT-936 OQ-1: move, not copy) — buffers this
  // level's user-tier clauses into the pending shared-filter draft and
  // commits it in the same call via `overrides`, since `setLevelFilter`'s
  // `setState` isn't visible in this closure until the next render. Mirrors
  // `AtlasComponent`'s `handleColumnFilterShare`.
  const handleShareLevelFilterWithView = useCallback(
    async (levelId: string, clauses: UnifiedFilters): Promise<void> => {
      setLevelFilter(levelId, clauses, 'view');
      // `attemptShare` (the popover) only distinguishes resolve from
      // reject — a `false` resolution (view save unavailable, no network
      // attempt) must reject too, or the popover would treat "did not
      // write" as "wrote" and clear its own local user-tier copy.
      const committed = await commitPendingSharedFilters({ [levelId]: clauses });
      if (!committed) throw new Error('Share with view is unavailable');
    },
    [setLevelFilter, commitPendingSharedFilters]
  );

  // ── Share (EAINT-793 Phase 4) ───────────────────────────────────────────────
  const handleShare = useCallback(() => {
    if (!runtimeFilters) return;

    // D-3: addressed to the context the sender was actually viewing, so a
    // recipient's shell — theirs or another shell on the same page — can tell
    // whether this link is meant for it.
    const addressedEnvelope: SharedFilterLinkEnvelope = { ...runtimeFilters, contextSystemName: activeContextSystemName };
    const encoded = encodeFilterLink(addressedEnvelope);
    if (encoded === null) {
      logAnalyticsEvent('context_filter_link_refused_oversize', undefined, {
        contextSystemName: activeContextSystemName ?? null,
        payloadBytes: filterLinkEncodedLength(addressedEnvelope),
      });
      toastRef.current?.show({
        severity: 'warn',
        summary: t('columnFilters.share.oversizeTitle'),
        detail: t('columnFilters.share.oversizeDetail', { limit: SHARE_LINK_MAX_CHARS }),
        life: 6000,
      });
      return;
    }

    const params = new URLSearchParams();
    if (activeViewSystemName) params.set('view', activeViewSystemName);
    params.set('f', encoded);
    const url = buildShareUrl(params);

    // Both the success toast and `context_filter_link_shared` hang off the
    // resolved write, never off reaching this line: `writeText` rejects on a
    // denied permission or an unfocused document, and `navigator.clipboard` is
    // `undefined` outright in an insecure context (which would throw
    // synchronously here). Firing the event unconditionally would report shares
    // that never made it to anyone's clipboard — and `payloadBytes` from those
    // is exactly the data the 1500-char budget is supposed to be re-judged on.
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(url))
      .then(() => {
        toastRef.current?.show({ severity: 'success', summary: t('columnFilters.share.copied'), life: 3000 });
        logAnalyticsEvent('context_filter_link_shared', undefined, {
          contextSystemName: activeContextSystemName ?? null,
          levelCount: Object.keys(runtimeFilters.byLevelId ?? {}).length,
          laneCounts: sumEnvelopeLaneCounts(runtimeFilters),
          payloadBytes: encoded.length,
          hasVariableToken: hasVariableToken(runtimeFilters),
        });
      })
      .catch(() => {
        toastRef.current?.show({
          severity: 'warn',
          summary: t('columnFilters.share.copyFailedTitle'),
          detail: t('columnFilters.share.copyFailedDetail'),
          life: 6000,
        });
        logAnalyticsEvent('context_filter_link_copy_failed', undefined, {
          contextSystemName: activeContextSystemName ?? null,
          payloadBytes: encoded.length,
        });
      });
  }, [runtimeFilters, activeViewSystemName, activeContextSystemName, buildShareUrl, logAnalyticsEvent, t]);

  // ── Dirty state ────────────────────────────────────────────────────────────
  const {
    isDirty: definitionIsDirty,
    dirtyKinds: definitionDirtyKinds,
    reset: resetDirtyState,
    buildSavePayload,
  } = useContextDirtyState(context, view);

  const isDirty = definitionIsDirty || hasPendingSharedFilters;
  const dirtyKinds = hasPendingSharedFilters
    ? [...definitionDirtyKinds, 'viewFilters' as const]
    : definitionDirtyKinds;

  const discardBothRails = useCallback(() => {
    resetDirtyState();
    discardPendingSharedFilters();
  }, [resetDirtyState, discardPendingSharedFilters]);

  useEffect(() => {
    discardBothRails();
  }, [activeContextSystemName, discardBothRails]);

  // ── Session layer (board arrangement — resize/hide/collapse/density) ───────
  const {
    session,
    patchView: patchSessionView,
    reset: resetSession,
  } = useViewState(activeContextSystemName, activeViewSystemName);

  // ── Context-switch session boundary (EAINT-789 Phase 5) ────────────────────
  const viewStateContextValue = useContext(ViewStateContext);
  const prevContextRef = useRef<string>(activeContextSystemName);

  useEffect(() => {
    const prev = prevContextRef.current;
    if (prev !== activeContextSystemName) {
      viewStateContextValue?.resetSessionForContext(prev);
    }
    prevContextRef.current = activeContextSystemName;
  }, [activeContextSystemName, viewStateContextValue]);

  // ── User overlay (personalization) ─────────────────────────────────────────
  const handlePersonalizationWriteError = useCallback(() => {
    toastRef.current?.show({
      severity: 'warn',
      summary: t('contextSwitcher.personalizationSaveError'),
      life: 4000,
    });
  }, [t]);

  const personalization = useViewPersonalization(
    apiClients,
    activeContextSystemName,
    activeViewSystemName,
    { onWriteError: handlePersonalizationWriteError }
  );

  useEffect(() => {
    if (!linkActive) {
      linkAnalyticsFiredForRef.current = false;
      linkActivatedAtRef.current = null;
      return;
    }
    if (linkActivatedAtRef.current === null) linkActivatedAtRef.current = Date.now();
    if (linkAnalyticsFiredForRef.current) return;

    // Hold the metric until the overlay read has actually resolved.
    // `personalization.overlay` starts as the EMPTY placeholder and is only
    // filled by an async GET, and this effect flushes just after mount — so on
    // a cold page load from a pasted link, which is the ONLY way this feature
    // is entered, the sample was always taken before the read returned.
    // `hadSuppressedUserLayer` therefore reported false for every recipient:
    // not a slightly noisy metric, but a confident report of the opposite of
    // the truth, on the number the spec nominates as the evidence that
    // suppression was worth building.
    if (!personalization.settled) return;

    linkAnalyticsFiredForRef.current = true;

    const hadSuppressedUserLayer =
      Object.keys(personalization.overlay.base.levelFilters ?? {}).length > 0 ||
      Object.keys(session.base.levelFilters ?? {}).length > 0;

    logAnalyticsEvent('context_filter_link_opened', undefined, {
      contextSystemName: activeContextSystemName ?? null,
      hadSuppressedUserLayer,
    });
    // `personalization.overlay`/`session` deliberately excluded — they are
    // read only at the instant of activation (what the recipient's layers
    // held BEFORE suppression), not tracked continuously; including them
    // would refire this on every board edit made while the link is active.
    // `personalization.settled` IS a dependency: it is the edge this effect
    // waits for, and it flips at most once per board.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkActive, personalization.settled, activeContextSystemName, logAnalyticsEvent]);

  const pendingResetRef = useRef<'rails' | 'personalization' | null>(null);

  const performResetChanges = useCallback(() => {
    discardBothRails();
    resetSession();
  }, [discardBothRails, resetSession]);

  const performResetPersonalization = useCallback(() => {
    // `void`: reset() handles its own failure through `onWriteError` and never
    // rejects, so there is nothing to await or catch here — marked explicitly
    // so it reads as deliberate rather than a dropped promise.
    void personalization.reset();
    resetSession();
  }, [personalization, resetSession]);

  useEffect(() => {
    if (linkActive || pendingResetRef.current === null) return;
    const pending = pendingResetRef.current;
    pendingResetRef.current = null;
    if (pending === 'rails') performResetChanges();
    else performResetPersonalization();
  }, [linkActive, performResetChanges, performResetPersonalization]);

  const handleResetChanges = useCallback(() => {
    if (linkActive) {
      pendingResetRef.current = 'rails';
      handleExitSharedLink();
      return;
    }
    performResetChanges();
  }, [linkActive, handleExitSharedLink, performResetChanges]);

  const handleResetPersonalization = useCallback(() => {
    if (linkActive) {
      pendingResetRef.current = 'personalization';
      handleExitSharedLink();
      return;
    }
    performResetPersonalization();
  }, [linkActive, handleExitSharedLink, performResetPersonalization]);

  // ── Config resolution: defaults → view → user → session ────────────────────
  const viewLayerViewConfig = useMemo<Partial<TreeMapViewConfig>>(() => {
    const stored = (view?.view_config ?? {}) as Partial<TreeMapViewConfig>;
    if (!inlineViewConfig) return stored;
    return {
      ...stored,
      ...inlineViewConfig,
      columnState: mergeRecordByKey(stored.columnState, inlineViewConfig.columnState),
    };
  }, [view, inlineViewConfig]);

  const viewLayerBaseConfig = useMemo<Partial<BaseViewConfig>>(() => {
    const stored = (view?.base_config ?? {}) as Partial<BaseViewConfig>;
    if (!inlineBaseConfig) return stored;
    return {
      ...stored,
      ...inlineBaseConfig,
      levelOverrides: mergeRecordByKey(stored.levelOverrides, inlineBaseConfig.levelOverrides),
    };
  }, [view, inlineBaseConfig]);

  const resolvedConfig = useMemo(
    () =>
      resolveViewConfig({
        defaults: { base: TREEMAP_DEFAULT_BASE_CONFIG, view: TREEMAP_DEFAULT_VIEW_CONFIG },
        view: { base: viewLayerBaseConfig, view: viewLayerViewConfig },
        user: { base: personalization.overlay.base, view: personalization.overlay.view },
        session: { base: session.base, view: session.view },
      }),
    [viewLayerBaseConfig, viewLayerViewConfig, personalization.overlay, session]
  );

  const mergedViewConfig = resolvedConfig.view as TreeMapViewConfig;
  const mergedBaseConfig = resolvedConfig.base;

  // ── Active context display name ────────────────────────────────────────────
  const activeContextDisplayName = useMemo(() => {
    if (!context) return activeContextSystemName;
    const lang = i18n.language;
    return (
      context.name?.translations?.[lang] ||
      context.name?.default ||
      activeContextSystemName
    );
  }, [context, activeContextSystemName, i18n.language]);

  // ── Filter bar (EAINT-789) ─────────────────────────────────────────────────
  const filterBarLevels = useMemo<ContextFilterBarLevelEntry[]>(() => {
    const levels = context?.definition?.levels ?? [];
    const lang = i18n.language;
    return levels.map((level) => ({
      levelId: level.id,
      levelLabel: level.label?.translations?.[lang] || level.label?.default || level.id,
      composed: levelFiltersByLevel[level.id] ?? { runtime: {}, display: {}, provenance: [] },
    }));
  }, [context, levelFiltersByLevel, i18n.language]);

  const filterCatalogSchemas = schemas as SchemaForFilters[];

  const filterPropertyOptions = useMemo(
    () => buildFilterPropertyOptions({ schemas: filterCatalogSchemas, t }),
    [filterCatalogSchemas, t]
  );
  const filterPerspectiveOptions = useMemo(
    () => buildFilterPerspectiveOptions({ schemas: filterCatalogSchemas, t }),
    [filterCatalogSchemas, t]
  );
  const filterRelationshipOptions = useMemo(
    () => buildFilterRelationshipOptions({ schemas: filterCatalogSchemas, t }),
    [filterCatalogSchemas, t]
  );

  // Resolves by `property` (the bare key every stored filter carries) first,
  // then by `aspectIdentity` (`<aspect>.<property>`) — the merged catalog
  // dedupes composed properties on the latter, so a resolved-off-another-
  // schema aspect still needs the identity form to land on the right option
  // (SC1).
  const getFilterPropertyLabel = useCallback(
    (propertyKey: string, aspect?: string) => {
      const byKey = filterPropertyOptions.find((o) => o.key === propertyKey);
      if (byKey) return byKey.label;
      if (aspect) {
        const byIdentity = filterPropertyOptions.find((o) => o.aspectIdentity === `${aspect}.${propertyKey}`);
        if (byIdentity) return byIdentity.label;
      }
      return propertyKey;
    },
    [filterPropertyOptions]
  );
  const getFilterPerspectiveLabel = useCallback(
    (systemName: string) => filterPerspectiveOptions.find((o) => o.systemName === systemName)?.label ?? systemName,
    [filterPerspectiveOptions]
  );
  const getFilterRelationshipLabel = useCallback(
    (systemName: string) => filterRelationshipOptions.find((o) => o.systemName === systemName)?.label ?? systemName,
    [filterRelationshipOptions]
  );
  const getFilterPropertyValueLabel = useCallback(
    (propertyKey: string, value: string) =>
      filterPropertyOptions
        .find((o) => o.key === propertyKey)
        ?.enumValues?.find((v) => v.value === value)?.label ?? value,
    [filterPropertyOptions]
  );

  const handleFilterBarSave = useCallback(
    (target: FilterSaveTarget) => {
      if (target !== 'view') return;
      void commitPendingSharedFilters().catch(() => undefined);
    },
    [commitPendingSharedFilters]
  );

  // ── Save (in place) ───────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!apiClients?.schemaClient || !context) return;

    const { contextUpdate } = buildSavePayload();

    if (!contextUpdate && !hasPendingSharedFilters) return;

    try {
      if (contextUpdate) {
        await updateContext(apiClients.schemaClient, activeContextSystemName, {
          definition: contextUpdate.definition,
        });
        resetDirtyState();
      }

      if (hasPendingSharedFilters) {
        const committed = await commitPendingSharedFilters();
        if (!committed) {
          throw new Error('Shared-view filter save is currently unavailable');
        }
      }

      toastRef.current?.show({
        severity: 'success',
        summary: t('contextSwitcher.saveSuccess'),
        life: 3000,
      });
    } catch (err: unknown) {
      const status =
        (err as { status?: number })?.status ??
        (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        toastRef.current?.show({
          severity: 'error',
          summary: t('contextSwitcher.savePermissionDenied'),
          life: 5000,
        });
      } else {
        toastRef.current?.show({
          severity: 'error',
          summary: t('contextSwitcher.saveError'),
          life: 4000,
        });
      }
      throw err;
    }
  }, [
    apiClients,
    context,
    activeContextSystemName,
    buildSavePayload,
    resetDirtyState,
    hasPendingSharedFilters,
    commitPendingSharedFilters,
    t,
  ]);

  // ── Save as copy ──────────────────────────────────────────────────────────
  const handleSaveAsCopy = useCallback(
    async ({ name, description: _description }: { name: string; description?: string }) => {
      if (!apiClients?.schemaClient || !context) return;

      const { contextUpdate } = buildSavePayload();

      try {
        const result = await cloneContext(apiClients.schemaClient, activeContextSystemName, {
          newName: { default: name },
          includeViews: !!activeViewSystemName,
          definitionOverride: contextUpdate?.definition,
        });

        const newSystemName = result.context.system_name;
        discardBothRails();
        setActiveContextSystemName(newSystemName);
        toastRef.current?.show({
          severity: 'success',
          summary: t('contextSwitcher.saveAsCopySuccess'),
          life: 3000,
        });
      } catch (err: unknown) {
        const status =
          (err as { status?: number })?.status ??
          (err as { response?: { status?: number } })?.response?.status;
        if (status === 409) {
          toastRef.current?.show({
            severity: 'error',
            summary: t('contextSwitcher.saveAsCopyNameExists'),
            life: 5000,
          });
        } else {
          toastRef.current?.show({
            severity: 'error',
            summary: t('contextSwitcher.saveAsCopyError'),
            life: 4000,
          });
        }
      }
    },
    [
      apiClients,
      context,
      activeContextSystemName,
      buildSavePayload,
      activeViewSystemName,
      discardBothRails,
      setActiveContextSystemName,
      t,
    ]
  );

  // ── Context switch — loads the context's DEFAULT rendering (clears any view) ─
  // A bypass path (EAINT-789 gap analysis G-5): clears both dirty rails, not
  // just the definition one, or a pending shared-filter buffer survives a
  // switch the guard already let through unguarded.
  const handleContextSelect = useCallback(
    (systemName: string, _displayName: string) => {
      discardBothRails();
      setActiveViewSystemName(null);
      setActiveContextSystemName(systemName);
      setViewEditorState(null);
      setContextEditorState(null);
    },
    [discardBothRails, setActiveContextSystemName]
  );

  // ── View switch — loads a saved rendering of (possibly another) context ──────
  // The same G-5 bypass path, for the same reason: both rails, not just one.
  const handleViewSelect = useCallback(
    (viewSystemName2: string, _viewDisplayName: string, contextSystemName2: string) => {
      discardBothRails();
      setActiveContextSystemName(contextSystemName2);
      setActiveViewSystemName(viewSystemName2);
      setViewEditorState(null);
      setContextEditorState(null);
    },
    [discardBothRails, setActiveContextSystemName]
  );

  // ── View editor overlay state ─────────────────────────────────────────────
  interface ViewEditorState {
    mode: 'new' | 'edit';
    viewSystemName?: string;
    contextSystemName: string;
    contextDisplayName: string;
    seedForm?: ContextViewFormState;
  }

  const [viewEditorState, setViewEditorState] = useState<ViewEditorState | null>(null);
  const [switcherRefreshKey, setSwitcherRefreshKey] = useState(0);

  // ── Inline context editor state ───────────────────────────────────────────
  interface ContextEditorState {
    contextSystemName: string;
  }
  const [contextEditorState, setContextEditorState] = useState<ContextEditorState | null>(null);

  const handleNewView = useCallback(
    (ctxSn: string, ctxDisplayName: string) => {
      const seed = currentRenderingToViewForm(mergedViewConfig, mergedBaseConfig, ctxSn);
      setViewEditorState({ mode: 'new', contextSystemName: ctxSn, contextDisplayName: ctxDisplayName, seedForm: seed });
    },
    [mergedViewConfig, mergedBaseConfig]
  );

  const handleEditView = useCallback(
    (viewSn: string, ctxSn: string) => {
      const ctxDisplayName = ctxSn === activeContextSystemName ? activeContextDisplayName : ctxSn;
      setViewEditorState({ mode: 'edit', viewSystemName: viewSn, contextSystemName: ctxSn, contextDisplayName: ctxDisplayName });
    },
    [activeContextSystemName, activeContextDisplayName]
  );

  const handleViewSaved = useCallback(
    (savedViewSn: string) => {
      if (!viewEditorState) return;
      setActiveContextSystemName(viewEditorState.contextSystemName);
      setActiveViewSystemName(savedViewSn);
      setSwitcherRefreshKey((k) => k + 1);
      setViewOverride(null);
      setViewRefreshKey((k) => k + 1);
      resetDirtyState();
      setViewEditorState(null);
    },
    [viewEditorState, setActiveContextSystemName, resetDirtyState]
  );

  const handleViewDeleted = useCallback(
    (deletedViewSn: string) => {
      if (deletedViewSn === activeViewSystemName) {
        setActiveViewSystemName(null);
      }
      setSwitcherRefreshKey((k) => k + 1);
      setViewEditorState(null);
    },
    [activeViewSystemName]
  );

  // ── "Edit in context" (D-G / spec § Widening) ──────────────────────────────
  const handleEditContext = useCallback(
    (_levelId: string) => {
      onEditContext?.(activeContextSystemName);
    },
    [activeContextSystemName, onEditContext]
  );

  /**
   * Everything the shell owes the user *regardless* of whether it has context
   * data yet — the toast host and the malformed-link notice — wrapping all four
   * return paths below.
   *
   * The notice must sit above the loading/error gates, not inside the success
   * path. Before Phase 4 it rendered in `TreeMapPage` as a sibling of the
   * organism, so the organism's own state could not hide it; moving it into the
   * shell put it behind three early returns. A shell whose context fetch fails
   * would then show only "couldn't load" while the hook had *already* stripped
   * the malformed `?f=` out of the URL — so the reader is never told their link
   * was broken and cannot recover it by reloading. With several shells on a
   * page the notice's owner is claimed in an effect, before render, so an owner
   * that happened to be loading would swallow the notice for every sibling too.
   */
  const withShellFrame = (content: ReactNode) => (
    <>
      <Toast ref={toastRef} />
      {sharedLink.malformedNotice && !malformedDismissed && (
        <MessageAtom
          severity="warn"
          text={t('columnFilters.sharedLink.malformed')}
          dismissible
          onDismiss={() => setMalformedDismissed(true)}
        />
      )}
      {content}
    </>
  );

  // ── Loading state ──────────────────────────────────────────────────────────
  if (contextLoading || viewLoading || !context) {
    return withShellFrame(
      <div className="tree-map tree-map--loading">
        <div className="tree-map__loading-message">
          <i className="pi pi-spin pi-spinner context-shell__state-spinner" />
          <p>{t('treeMap.loading.template')}</p>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (contextError) {
    const isDevelopment = process.env.NODE_ENV === 'development';
    return withShellFrame(
      <div className="tree-map tree-map--error">
        <div className="tree-map__error-message">
          <i className="pi pi-info-circle context-shell__state-icon" />
          <h3>{t('treeMap.errors.loadTemplateFailed')}</h3>
          <p className="tree-map__error-detail">{t('treeMap.errors.tryAgainLater')}</p>
          {isDevelopment && <p className="tree-map__error-technical">{contextError}</p>}
          <ButtonAtom
            variant="primary"
            className="context-shell__retry-button"
            onClick={() => globalThis.location.reload()}
            label={t('treeMap.errors.retryButton')}
          />
        </div>
      </div>
    );
  }

  if (!rootSphereId) {
    return withShellFrame(
      <div className="tree-map tree-map--error">
        <div className="tree-map__error-message">
          <i className="pi pi-info-circle context-shell__state-icon" />
          <h3>{t('treeMap.errors.somethingWentWrong')}</h3>
          <p className="tree-map__error-detail">{t('treeMap.errors.missingParams')}</p>
        </div>
      </div>
    );
  }

  const body: ContextShellBodyState = {
    context,
    viewConfig: mergedViewConfig,
    baseConfig: mergedBaseConfig,
    levelFilters: levelFiltersByLevel,
    runtimeFilters,
    filterSaveTargets,
    onSetLevelFilter: handleSetLevelFilterWithLinkExit,
    onShareWithView: handleShareLevelFilterWithView,
    linkSuppressed: linkActive,
    onExitSharedLink: linkActive ? handleExitSharedLink : undefined,
    canEditContext,
    onEditContext: handleEditContext,
    patchSessionView,
    patchPersonalizationView: personalization.patchView,
    filterPropertyOptions,
    filterPerspectiveOptions,
    getFilterPropertyLabel,
    getFilterPerspectiveLabel,
    getFilterPropertyValueLabel,
  };

  // Switcher-only row — shown above the inline editor (filter bar is irrelevant while editing a view).
  const switcherBarNode = (
    <>
      {linkActive && (
        <SharedLinkBannerMolecule
          hasVariableToken={linkHasVariableToken}
          notice={sharedLinkNotice}
          onExit={handleExitSharedLink}
          onRetry={sharedLinkNotice === 'viewUnavailable' ? handleRetrySharedLinkView : undefined}
          retrying={viewRefreshing}
        />
      )}
      <ContextSwitcherToolbarMolecule
        activeContextSystemName={activeContextSystemName}
        activeContextDisplayName={activeContextDisplayName}
        defaultContextSystemName={defaultContextSystemName}
        activeViewSystemName={activeViewSystemName}
        activeViewDisplayName={activeViewDisplayName}
        viewType="treemap"
        sphereId={rootSphereId}
        rootLabel={rootSphereName}
        organizationId={organizationId}
        canEditActiveContext={canEditActiveContext}
        isDirty={isDirty}
        dirtyKinds={dirtyKinds}
        onSave={handleSave}
        onSaveAsCopy={handleSaveAsCopy}
        onResetChanges={handleResetChanges}
        onContextSelect={handleContextSelect}
        onViewSelect={handleViewSelect}
        onNewView={handleNewView}
        onEditView={handleEditView}
        onEditContext={onEditContext}
        onFilterSettings={onFilterSettings}
        onNewContext={onNewContext}
        onManageContexts={onManageContexts}
        switcherRefreshKey={switcherRefreshKey}
      />
    </>
  );

  const contextBarNode = (
    <>
      {switcherBarNode}
      <ContextFilterBarMolecule
        contextSystemName={activeContextSystemName}
        levels={filterBarLevels}
        degraded={degradedLevelFilters}
        pendingSharedFilterLevelIds={pendingSharedFilterLevelIds}
        saveTargets={filterSaveTargets}
        onSave={handleFilterBarSave}
        onReset={resetLevelFilters}
        onShare={handleShare}
      >
        {filterBarLevels.map(({ levelId, levelLabel, composed }) => (
          <FilterSummaryGroupMolecule
            key={levelId}
            contextSystemName={activeContextSystemName}
            levelId={levelId}
            levelLabel={levelLabel}
            composed={composed}
            degraded={degradedLevelFilters}
            canRemoveLayer={canRemoveLayer}
            onRemove={removeFilterClause}
            getPropertyLabel={getFilterPropertyLabel}
            getPerspectiveLabel={getFilterPerspectiveLabel}
            getRelationshipLabel={getFilterRelationshipLabel}
            getPropertyValueLabel={getFilterPropertyValueLabel}
          />
        ))}
      </ContextFilterBarMolecule>
    </>
  );

  // When either inline editor is open, inject only the switcher (no filter bar)
  // into the TreeMap's header slot so the TreeMap still renders its full
  // header — including the right-side view-toggle buttons — while the board
  // content is hidden via CSS.
  // Opening a context prefers the inline editor when the host supplies one,
  // falls back to the host's own navigation, and is absent when neither
  // exists. Named so the three-way choice is not a nested ternary in JSX.
  const resolveOpenContextHandler = (contextSystemName: string): (() => void) | undefined => {
    if (renderContextEditor) return () => setContextEditorState({ contextSystemName });
    if (onEditContext) return () => onEditContext(contextSystemName);
    return undefined;
  };

  // While either editor is open the bar shows the switcher rather than the
  // filter bar; in 'slot' placement the host renders no bar at all.
  const editorIsOpen = Boolean(viewEditorState || contextEditorState);
  const slotContextBar = editorIsOpen ? switcherBarNode : contextBarNode;
  const chrome: ContextShellChrome = {
    contextBar: chromePlacement === 'slot' ? slotContextBar : null,
    body,
  };

  return withShellFrame(
    <div className={`tree-map-organism${viewEditorState ? ' tree-map-organism--editor-open' : ''}`}>
      {!viewEditorState && chromePlacement === 'above' && contextBarNode}

      {contextEditorState && renderContextEditor ? (
        // Inline context editor — same card structure as the view editor:
        // TreeMap renders its header (switcher chips + right view-toggle buttons),
        // board content is hidden via CSS, context editor fills below.
        <div className="tree-map-organism__editor-card">
          {children(chrome)}
          {renderContextEditor({
            contextSystemName: contextEditorState.contextSystemName,
            onBack: () => setContextEditorState(null),   // back to view editor
            onClose: () => {
              setContextEditorState(null);
              setViewEditorState(null);                  // close both editors
            },
          })}
        </div>
      ) : viewEditorState ? (
        // Unified card: TreeMap renders its header (left switcher + right
        // view-toggle buttons); board content is hidden via CSS. The inline
        // editor fills the remaining space below the header.
        <div className="tree-map-organism__editor-card">
          {children(chrome)}
          <ContextViewEditorOverlayOrganism
            inline
            mode={viewEditorState.mode}
            contextSystemName={viewEditorState.contextSystemName}
            contextDisplayName={viewEditorState.contextDisplayName}
            viewSystemName={viewEditorState.viewSystemName}
            seedForm={viewEditorState.seedForm}
            onSaved={handleViewSaved}
            onDeleted={handleViewDeleted}
            onClose={() => setViewEditorState(null)}
            onNavigateToLibrary={onManageContexts}
            onOpenContext={resolveOpenContextHandler(viewEditorState.contextSystemName)}
            hasPersonalizationOverlay={personalization.hasOverlay}
            onResetPersonalization={handleResetPersonalization}
          />
        </div>
      ) : (
        children(chrome)
      )}
    </div>
  );
};

export default ContextShellOrganism;
